import React from 'react';
import { Helmet } from 'react-helmet';
import { Database } from '@/integrations/supabase/types';

// Update the type to match the one used in BlogOpenGraph
type BlogPostWithAuthor = Partial<Database['public']['Tables']['blog_posts']['Row']> & {
  author?: {
    first_name?: string;
    last_name?: string;
  } | null;
  tags?: { id: string; name: string }[];
};

interface BlogStructuredDataProps {
  post: BlogPostWithAuthor;
  baseUrl: string;
}

export function BlogStructuredData({ post, baseUrl }: BlogStructuredDataProps) {
  // Determine values with fallbacks
  const title = post.seo_title || post.title || '';
  const description = post.seo_description || post.excerpt || `Read more about ${post.title || ''}`;
  const imageUrl = post.featured_image || `${baseUrl}/og-image.png`;
  
  // Get publication date
  const publishedDate = post.created_at 
    ? new Date(post.created_at).toISOString() 
    : new Date().toISOString();
    
  // Get modified date
  const modifiedDate = post.updated_at
    ? new Date(post.updated_at).toISOString()
    : publishedDate;
  
  // Get author name
  const authorName = post.author 
    ? `${post.author.first_name || ''} ${post.author.last_name || ''}`.trim() 
    : 'Trotting the World';
  
  // Calculate word count if content is available
  const wordCount = post.content ? post.content.split(/\s+/).length : 0;
  
  // Extract keywords from tags or SEO keywords
  const keywords = post.tags 
    ? post.tags.map(tag => tag.name).join(', ')
    : post.seo_keywords || '';
  
  // Create enhanced structured data object
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${baseUrl}/blog/${post.slug || ''}`
    },
    "headline": title,
    "description": description,
    "image": {
      "@type": "ImageObject",
      "url": imageUrl,
      "width": 1200,
      "height": 630
    },
    "author": {
      "@type": "Person",
      "name": authorName
    },
    "publisher": {
      "@type": "Organization",
      "name": "Trotting the World",
      "logo": {
        "@type": "ImageObject",
        "url": `${baseUrl}/logo.png`,
        "width": 600,
        "height": 60
      }
    },
    "datePublished": publishedDate,
    "dateModified": modifiedDate,
    "wordCount": wordCount,
    "keywords": keywords,
    "articleSection": post.tags && post.tags.length > 0 ? post.tags[0].name : "Travel",
    "inLanguage": "en-US"
  };
  
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}
